import { Tabs, Title } from "@mantine/core";
import { FeatureGate } from "@src/components/plan/feature-gate/feature-gate";
import { FeatureEntity, SupportedFeature } from "@src/gql/graphql";
import clsx from "clsx";
import { CollectTabContent } from "./collect-tab-content";
import { useShareWhiteboardPanelState } from "./hooks/use-share-whiteboard-panel-state";
import { PublishTabContent } from "./publish-tab-content";
import { ShareWhiteboardInviteForm } from "./share-whiteboard-invite-form";
import type { ShareableEntity } from "./shareable-entity";

type ShareWhiteboardPanelContentProps = {
	whiteboard: ShareableEntity;
};

export function ShareWhiteboardPanelContent({
	whiteboard,
}: ShareWhiteboardPanelContentProps) {
	const panel = useShareWhiteboardPanelState({
		whiteboardId: whiteboard.id,
		publication: whiteboard.publication,
		intake: whiteboard.intake,
	});

	return (
		<div>
			<div className="mb-4">
				<Title order={3}>Share Whiteboard</Title>
			</div>

			<Tabs
				value={panel.activeTab}
				onChange={panel.setActiveTab}
				classNames={{
					root: "flex flex-col",
					list: "mb-2 border-b border-border-subtle",
					tab: clsx(
						"pb-3 pt-2 font-medium text-text-secondary hover:text-text-primary",
						"data-[active]:border-brand-primary data-[active]:text-brand-primary",
					),
					panel: "pt-1 pb-2",
				}}
			>
				<Tabs.List>
					<Tabs.Tab value="share">Share</Tabs.Tab>
					<Tabs.Tab value="publish" className="transition-colors duration-200">
						Publish
					</Tabs.Tab>
					<Tabs.Tab value="collect" className="transition-colors duration-200">
						Collect Notes
					</Tabs.Tab>
				</Tabs.List>

				<Tabs.Panel value="share">
					<FeatureGate
						feature={SupportedFeature.AddBoardEditors}
						entity={{
							id: whiteboard.id,
							type: FeatureEntity.Whiteboard,
						}}
						render={(permissions) => (
							<ShareWhiteboardInviteForm
								id={whiteboard.id}
								permissions={permissions}
							/>
						)}
					/>
				</Tabs.Panel>

				<Tabs.Panel value="publish">
					<PublishTabContent
						boardId={panel.boardDocumentId}
						isPublished={panel.isPublished}
						isLoading={panel.isLoading}
						allowTemplate={panel.allowTemplate}
						permission={panel.permission}
						clipboardPublishedLink={panel.clipboardPublishedLink}
						entityName={whiteboard.name}
						entityLabel="board"
						isLinkCopiedTooltipOpen={panel.isLinkCopiedTooltipOpen}
						onCopyTooltipTrigger={panel.handleCopyPublishLinkTooltip}
						onSwitchChange={panel.handleSwitchChange}
						onTemplateChange={panel.handleTemplateChange}
						onPermissionChange={panel.handlePermissionChange}
						showTemplateOption
					/>
				</Tabs.Panel>

				<Tabs.Panel value="collect">
					<CollectTabContent
						boardId={panel.boardDocumentId}
						isPublished={panel.isPublished}
						isCollectNotesEnabled={panel.isCollectNotesEnabled}
						isLoading={panel.isLoading}
						collectNotesLink={panel.collectNotesLink}
						whiteboardName={whiteboard.name}
						isCollectLinkCopiedTooltipOpen={
							panel.isCollectLinkCopiedTooltipOpen
						}
						onPublishAndCollect={panel.handlePublishAndCollect}
						onCollectNotesSwitchChange={panel.handleCollectNotesSwitchChange}
						onCopyTooltipTrigger={panel.handleCopyCollectLinkTooltip}
					/>
				</Tabs.Panel>
			</Tabs>
		</div>
	);
}

/** @deprecated Use ShareableEntity */
export type UniversalShareableEntity = ShareableEntity;
